import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import BackToDocumentary from "../../shared/BackToDocumentary/BackToDocumentary";
import { projects } from "../../../content";
import type { Project, ProjectCategory } from "../../../types/content";

/**
 * ============================================================================
 * Projects — the /projects route.
 * ============================================================================
 *
 * Client and delivery builds that are not part of the owned SFT product suite.
 * Direct consumer of the content layer, no bridge (V.4). To change what this
 * page says, edit `src/content/data/projects.json` — not this file.
 *
 * SPLIT FROM THE OLD PLATFORMS SECTION ON 2026-08-20 (D-067), which carried all
 * 28 rows behind one heading. The nineteen here are a different claim from the
 * nine on /products: delivered for a client, rather than owned as a product.
 *
 * NOTE WHAT THE SPLIT IS NOT. It is not "Shyam Future Tech vs elsewhere" —
 * seven of these nineteen were delivered AT Shyam Future Tech and still belong
 * here, because they are client work rather than suite products. Employer is
 * not the discriminator; ownership is.
 *
 * THE FILTER DEFAULTS TO "All", WHICH IS WHAT THE PRE-RENDER CAPTURES. Every
 * project is therefore in the built HTML and reachable by a crawler; the filter
 * narrows an already-complete page rather than being the only way to see a
 * card. A filter that hid rows from the pre-render would be D-065 with a
 * different mechanism.
 */

/** Filter state: a real category, or the unfiltered view. */
type Filter = ProjectCategory | "All";

/**
 * Display order, and the order of the filter pills.
 *
 * Deliberately NOT the array order in the JSON. Healthcare leads because the
 * active search targets US healthcare insurance technology and the strongest
 * evidence in the file sits there ($3M+, 21+ insurers, EDI 834). This
 * is positioning, so it lives in the component, not the content layer.
 */
const CATEGORY_ORDER: ProjectCategory[] = ["healthcare-regulated", "enterprise-client", "govtech", "ai-innovation"];

export const CATEGORY_LABEL: Record<ProjectCategory, string> = {
	"healthcare-regulated": "Healthcare (Regulated)",
	"enterprise-client": "Enterprise Client",
	govtech: "GovTech",
	"ai-innovation": "AI & Innovation"
};

/**
 * Accent per category. Written as whole class strings because Tailwind's JIT
 * scans source text — a constructed name like `text-${x}-300` is never emitted.
 */
export const CATEGORY_ACCENT: Record<ProjectCategory, string> = {
	"healthcare-regulated":
		"border-[var(--cat-emerald-border)] text-[var(--cat-emerald-text)] bg-[var(--cat-emerald-fill)]",
	"enterprise-client": "border-[var(--cat-sky-border)] text-[var(--cat-sky-text)] bg-[var(--cat-sky-fill)]",
	govtech: "border-[var(--cat-amber-border)] text-[var(--cat-amber-text)] bg-[var(--cat-amber-fill)]",
	"ai-innovation": "border-[var(--cat-fuchsia-border)] text-[var(--cat-fuchsia-text)] bg-[var(--cat-fuchsia-fill)]"
};

/**
 * The page's standfirst — used verbatim as this route's meta description
 * (D-044). Asserts no count; the route's description appends computed ones.
 */
export const PROJECTS_STANDFIRST =
	"Client and delivery builds outside the owned product suite — regulated healthcare, government technology, AI and enterprise systems, each with the outcome it produced.";

/** Categories actually present, in ranked order — a pill with nothing behind it is never offered. */
const presentCategories: ProjectCategory[] = CATEGORY_ORDER.filter((cat: ProjectCategory) =>
	projects.some((p: Project) => p.category === cat)
);

const FILTERS: Filter[] = ["All", ...presentCategories];

/** Ranked once at module scope: the ordering never changes at runtime. */
const orderedProjects: Project[] = [...projects].sort(
	(a: Project, b: Project) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category)
);

const countOf = (filter: Filter): number =>
	filter === "All" ? projects.length : projects.filter((p: Project) => p.category === filter).length;

const ProjectCard = ({ project }: { project: Project }): JSX.Element => (
	/* THE `id` IS THE LANDING SITE FOR THE HOME TEASER (D-100), on the pattern
	   `/products` took in D-099 and `Stories.tsx` took before it. Each teaser
	   card links to `/projects#<id>`; `ScrollManager` resolves a hash on a
	   client-side route change with `getElementById`, so without this attribute
	   the link navigates correctly and then sits at the top of the page — which
	   reads as a broken link rather than as a missing anchor.

	   EVERY project is anchored, not only the four the teaser links to: an
	   unanchored record cannot be linked to later, and the gate asserts the join
	   in both directions.

	   ONE HAZARD IS SPECIFIC TO THIS PAGE and is why the filter's initial state
	   matters. `/projects` filters, and a card the active filter excludes is not
	   in the document at all — so a hash pointing at it would find nothing. The
	   filter opens on "All", which is what makes every anchor resolvable on
	   arrival; a default of anything else would silently break a subset of these
	   links. */
	<div id={project.id} className="card-shell scroll-mt-24">
		<article className="card-face flex h-full flex-col p-6">
			<span
				className={`pill-tint pill-tint-upper pill-tint-wrap self-start ${CATEGORY_ACCENT[project.category]}`}
			>
				{CATEGORY_LABEL[project.category]}
			</span>

			<h2 className="mt-4 text-xl font-bold text-[var(--text-strong)]">{project.name}</h2>

			<p className="mt-2 text-sm text-[var(--text-muted)]">
				{project.organization} · {project.role}
			</p>

			<p className="mt-4 text-sm leading-relaxed text-[var(--text-secondary)]">{project.description}</p>

			{project.outcomes.length > 0 && (
				<ul className="mt-5 space-y-2">
					{project.outcomes.map((outcome: string) => (
						<li key={outcome} className="flex gap-2 text-sm font-medium text-[var(--text-primary)]">
							<span aria-hidden="true" className="text-[var(--accent-marker)]">
								→
							</span>
							<span>{outcome}</span>
						</li>
					))}
				</ul>
			)}

			{/* Pushed to the foot so cards in a row align on their tag rail. */}
			<div className="mt-auto flex flex-wrap gap-2 pt-5">
				{project.tags.map((tag: string) => (
					<span key={tag} className="chip-quiet">
						{tag}
					</span>
				))}
			</div>
		</article>
	</div>
);

export default function Projects(): JSX.Element {
	const [activeFilter, setActiveFilter] = useState<Filter>("All");

	const visible = useMemo(
		() =>
			activeFilter === "All"
				? orderedProjects
				: orderedProjects.filter((p: Project) => p.category === activeFilter),
		[activeFilter]
	);

	return (
		<section className="section-shell" aria-labelledby="projects-heading">
			<div className="section-rail">
				<p className="section-eyebrow">Client &amp; Delivery Work</p>

				{/* THE ROUTE'S ONLY <h1>. Every route ships exactly one (D-042). */}
				<h1 id="projects-heading" className="heading">
					<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
						Projects
					</span>
				</h1>

				<p className="section-standfirst">{PROJECTS_STANDFIRST}</p>

				<div
					className="mt-8 flex flex-wrap justify-center gap-3"
					role="group"
					aria-label="Filter projects by category"
				>
					{FILTERS.map((filter: Filter) => (
						<button
							key={filter}
							type="button"
							onClick={() => setActiveFilter(filter)}
							aria-pressed={filter === activeFilter}
							className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 ${
								filter === activeFilter
									? "border-[var(--accent-marker)] bg-[var(--accent-marker-fill)] text-[var(--text-strong)]"
									: "border-[var(--border-pill)] text-[var(--text-muted)] hover:border-[var(--border-card-hover)] hover:text-[var(--text-bright)]"
							}`}
						>
							{filter === "All" ? "All" : CATEGORY_LABEL[filter]}
							<span className="ml-2 text-xs opacity-70">{countOf(filter)}</span>
						</button>
					))}
				</div>

				{/* `auto-rows-fr` for the reason Services and Products gained it
				    (D-099): a grid row stretches its items to the tallest of THAT
				    row, so cards matched their row-mates and not the rows above or
				    below. `grid-balanced` is deliberately NOT here — the visible
				    count changes with the filter, so whether the last row holds one
				    card is a property of the reader's selection rather than of the
				    layer, and a rule that centres a card only under some filters
				    would read as a bug. */}
				<div className="mt-12 grid w-full auto-rows-fr grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 xl:grid-cols-3 xl:gap-8">
					{visible.map((project: Project) => (
						<ProjectCard key={project.id} project={project} />
					))}
				</div>

				<div className="mt-16 flex flex-wrap justify-center gap-6">
					<Link
						to="/products"
						className="inline-block min-h-[24px] text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
					>
						← Owned products
					</Link>
					<BackToDocumentary />
				</div>
			</div>
		</section>
	);
}
